import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle2, AlertCircle, XCircle, Loader2, Users,
  BookOpen, Clock, Activity, TrendingUp, ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';
import OperationsLayout, { PageHeader } from './OperationsLayout';
import { getBaseline, listIncidents, getPromotionQueue, getKnowledgeMetrics } from '@/lib/operationsApi';

export default function PilotHealthPage() {
  const navigate = useNavigate();
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getBaseline(),
      listIncidents({ limit: 200 }),
      getPromotionQueue("pending_review"),
      getKnowledgeMetrics(),
    ])
      .then(([baseline, incidents, queue, knowledge]) => {
        setData({ baseline, incidents: incidents.items || [], queue: queue.items || [], knowledge });
      })
      .catch(e => toast.error("Failed to load pilot health: " + e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <OperationsLayout>
        <PageHeader title="Pilot Health" subtitle="Administrator view — pilot readiness and adoption monitoring" />
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </OperationsLayout>
    );
  }

  const { baseline, incidents, queue, knowledge } = data;
  const enabledAt = baseline?.queryverse_enabled_at ? new Date(baseline.queryverse_enabled_at) : null;
  const pilotWeeks = 6;
  const pilotEndDate = enabledAt ? new Date(enabledAt.getTime() + pilotWeeks * 7 * 24 * 3600 * 1000) : null;
  const now = new Date();
  const daysElapsed = enabledAt ? Math.floor((now - enabledAt) / (24 * 3600 * 1000)) : null;
  const daysRemaining = pilotEndDate ? Math.max(0, Math.ceil((pilotEndDate - now) / (24 * 3600 * 1000))) : null;
  const pilotPct = (daysElapsed != null && daysRemaining != null)
    ? Math.min(100, Math.round(daysElapsed / (pilotWeeks * 7) * 100))
    : 0;

  // Engineer activity analysis
  const engineerMap = {};
  for (const inc of incidents) {
    const eng = inc.assigned_engineer || "Unassigned";
    if (!engineerMap[eng]) engineerMap[eng] = { name: eng, incidents: 0, closed: 0, lastSeen: null };
    engineerMap[eng].incidents++;
    if (inc.status === "closed") engineerMap[eng].closed++;
    if (inc.updated_at) {
      const d = new Date(inc.updated_at);
      if (!engineerMap[eng].lastSeen || d > engineerMap[eng].lastSeen) {
        engineerMap[eng].lastSeen = d;
      }
    }
  }
  const engineers = Object.values(engineerMap);
  const activeEngineers = engineers.filter(e => {
    if (!e.lastSeen) return false;
    return (now - e.lastSeen) < 7 * 24 * 3600 * 1000;
  });

  // Incidents without events in last 24h (stale)
  const stale = incidents.filter(i =>
    i.status !== "closed" &&
    i.updated_at &&
    (now - new Date(i.updated_at)) > 24 * 3600 * 1000
  );

  // Closed incidents without promotion (missing knowledge)
  const closedNoPromotion = incidents.filter(i =>
    i.status === "closed" && !i.knowledge_promotion_id
  );

  // Data quality score
  const closedWithRca = incidents.filter(i => i.status === "closed" && i.root_cause && i.resolution).length;
  const totalClosed   = incidents.filter(i => i.status === "closed").length;
  const dataQualityPct = totalClosed > 0 ? Math.round(closedWithRca / totalClosed * 100) : null;

  // Warnings
  const warnings = [];
  if (!baseline?.baseline_established) {
    warnings.push({ level: "error", text: "Baseline not set — before/after comparison is impossible until set." });
  }
  if (queue.length > 5) {
    warnings.push({ level: "warn", text: `Knowledge queue has ${queue.length} items pending SME review — clear within 48h to maintain data quality.` });
  }
  if (stale.length > 0) {
    warnings.push({ level: "warn", text: `${stale.length} open incident(s) have had no activity in >24h — check with assigned engineer.` });
  }
  if (closedNoPromotion.length > 3) {
    warnings.push({ level: "warn", text: `${closedNoPromotion.length} closed incidents have not been nominated for the knowledge base — adoption may be low.` });
  }
  if (incidents.length < 5 && daysElapsed > 7) {
    warnings.push({ level: "error", text: "Fewer than 5 incidents logged after 7 days — pilot may not generate enough evidence. Check engineer adoption." });
  }
  if (dataQualityPct != null && dataQualityPct < 80) {
    warnings.push({ level: "warn", text: `${100 - dataQualityPct}% of closed incidents are missing root cause or resolution — data quality will affect outcome report.` });
  }

  return (
    <OperationsLayout>
      <PageHeader
        title="Pilot Health"
        subtitle="Administrator view — pilot readiness and adoption monitoring"
      />

      <div className="p-6 space-y-6">

        {/* Pilot timeline */}
        <div className="bg-card border border-border rounded-lg px-5 py-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-sm font-semibold">6-Week Pilot Progress</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {enabledAt
                  ? `Started ${enabledAt.toLocaleDateString()} · ${daysElapsed} days elapsed · ${daysRemaining} days remaining`
                  : "Pilot has not started — baseline not set"
                }
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-foreground">{pilotPct}%</div>
              <div className="text-xs text-muted-foreground">complete</div>
            </div>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className="bg-primary rounded-full h-2 transition-all"
              style={{ width: `${pilotPct}%` }}
            />
          </div>
          {pilotEndDate && daysRemaining > 0 && (
            <div className="text-xs text-muted-foreground mt-2">
              Target completion: {pilotEndDate.toLocaleDateString()}
            </div>
          )}
          {daysRemaining === 0 && (
            <div className="text-xs text-green-600 dark:text-green-400 font-medium mt-2">
              Pilot period complete — generate executive report
            </div>
          )}
        </div>

        {/* Warnings */}
        {warnings.length > 0 && (
          <div className="space-y-2">
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Warnings</div>
            {warnings.map((w, i) => (
              <div
                key={i}
                className={`flex items-start gap-2 px-3 py-2.5 rounded-lg text-sm ${
                  w.level === "error"
                    ? "bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400"
                    : "bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-400"
                }`}
              >
                {w.level === "error"
                  ? <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  : <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                }
                {w.text}
              </div>
            ))}
          </div>
        )}

        {/* KPI row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <HealthCard
            icon={Activity}
            label="Total Incidents"
            value={incidents.length}
            sub={`${totalClosed} closed, ${incidents.length - totalClosed} active`}
            ok={incidents.length >= 10}
          />
          <HealthCard
            icon={BookOpen}
            label="Verified Knowledge"
            value={knowledge?.verified_incidents ?? 0}
            sub={`${knowledge?.promotion_rate_pct ?? 0}% promotion rate`}
            ok={(knowledge?.verified_incidents ?? 0) >= 5}
          />
          <HealthCard
            icon={Users}
            label="Active Engineers"
            value={`${activeEngineers.length} / ${engineers.length}`}
            sub="logged incident in last 7 days"
            ok={activeEngineers.length > 0}
          />
          <HealthCard
            icon={ShieldCheck}
            label="Data Quality"
            value={dataQualityPct != null ? `${dataQualityPct}%` : "—"}
            sub="closed incidents with full RCA"
            ok={dataQualityPct == null || dataQualityPct >= 80}
          />
        </div>

        {/* Checklist */}
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-border text-sm font-semibold">
            Pilot Readiness Checklist
          </div>
          <div className="divide-y divide-border">
            <CheckItem
              label="Baseline date set"
              ok={baseline?.baseline_established}
              okText={`Set ${enabledAt?.toLocaleDateString()}`}
              failText="Required before pilot evidence is valid"
            />
            <CheckItem
              label="≥ 10 incidents logged"
              ok={incidents.length >= 10}
              okText={`${incidents.length} incidents`}
              failText={`${incidents.length} logged — need ${10 - incidents.length} more`}
            />
            <CheckItem
              label="≥ 5 verified knowledge articles"
              ok={(knowledge?.verified_incidents ?? 0) >= 5}
              okText={`${knowledge?.verified_incidents} articles approved`}
              failText={`${knowledge?.verified_incidents ?? 0} approved — SME review needed`}
            />
            <CheckItem
              label="Knowledge queue clear (≤ 2 pending)"
              ok={queue.length <= 2}
              okText="Queue clear"
              failText={`${queue.length} items pending SME review`}
              action={queue.length > 2 ? () => navigate("/operations/knowledge") : undefined}
              actionLabel="Review queue"
            />
            <CheckItem
              label="No stale incidents (>24h without activity)"
              ok={stale.length === 0}
              okText="All incidents have recent activity"
              failText={`${stale.length} incident(s) stale`}
              action={stale.length > 0 ? () => navigate("/operations/incidents") : undefined}
              actionLabel="View incidents"
            />
            <CheckItem
              label="Data quality ≥ 80% (RCA + resolution present)"
              ok={dataQualityPct == null || dataQualityPct >= 80}
              okText={`${dataQualityPct}% of closed incidents have full RCA`}
              failText={`${dataQualityPct}% — some closed incidents are missing root cause`}
            />
          </div>
        </div>

        {/* Engineer activity table */}
        {engineers.length > 0 && (
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-border text-sm font-semibold">
              Engineer Adoption
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">Engineer</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Incidents</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Closed</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Last active</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {engineers.sort((a, b) => b.incidents - a.incidents).map(eng => {
                  const isActive = eng.lastSeen && (now - eng.lastSeen) < 7 * 24 * 3600 * 1000;
                  const daysSince = eng.lastSeen ? Math.floor((now - eng.lastSeen) / (24 * 3600 * 1000)) : null;
                  return (
                    <tr key={eng.name} className="hover:bg-accent/30">
                      <td className="px-4 py-2.5 font-medium text-foreground">{eng.name}</td>
                      <td className="px-3 py-2.5 text-muted-foreground">{eng.incidents}</td>
                      <td className="px-3 py-2.5 text-muted-foreground">{eng.closed}</td>
                      <td className="px-3 py-2.5 text-muted-foreground text-xs">
                        {daysSince != null ? `${daysSince}d ago` : "—"}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                          isActive
                            ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                            : "bg-muted text-muted-foreground"
                        }`}>
                          {isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </OperationsLayout>
  );
}

function HealthCard({ icon: Icon, label, value, sub, ok }) {
  return (
    <div className={`bg-card border rounded-lg px-4 py-4 ${ok ? "border-border" : "border-amber-200 dark:border-amber-800"}`}>
      <div className="flex items-center justify-between mb-2">
        <Icon className="w-4 h-4 text-muted-foreground" />
        {ok
          ? <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
          : <AlertCircle className="w-3.5 h-3.5 text-amber-500" />
        }
      </div>
      <div className="text-2xl font-bold text-foreground">{value}</div>
      <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function CheckItem({ label, ok, okText, failText, action, actionLabel }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      {ok
        ? <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
        : <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
      }
      <div className="flex-1 min-w-0">
        <div className="text-sm text-foreground">{label}</div>
        <div className={`text-xs mt-0.5 ${ok ? "text-muted-foreground" : "text-red-500 dark:text-red-400"}`}>
          {ok ? okText : failText}
        </div>
      </div>
      {action && (
        <button
          onClick={action}
          className="text-xs text-primary hover:underline flex-shrink-0"
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
}
